import { EventEmitter, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { DebouncedFunc } from 'lodash';
import { INFOS } from '../common/utils/notification-constants';
import {
  OverlayCanvas,
  RtEvent,
} from '../sessions/session/overlay-canvas/overlay-canvas.component';
import {
  RealTimeDrawingHandler,
  RealTimeMagicInkHandler,
  RealTimePointerHandler,
} from '../sessions/session/wb-toolbar/toolbars/realtime-common';
import { ToasterPopupStyle } from '../ui/notification-toaster/custom-notification-toastr/custom-notification-toastr.component';
import { InspectModeRepository } from '../state/inspect-mode-repository.service';
import { SpaceRepository } from '../state/space.repository';
import { YCanvas } from '../sessions/session/wb-canvas/fabric-utils';
import { FlagsService } from './flags.service';
import {
  NotificationIconTypes,
  NotificationToasterService,
  NotificationType,
  V2NotificationDataBuilder,
} from './notification-toaster.service';
import { ScribbleType, SessionSharedDataService } from './session-shared-data.service';
import { SessionsVptService } from './sessions-vpt.service';

export function isRenderableCanvas(canvas?: fabric.StaticCanvas): boolean {
  return !!canvas && !!(canvas as any).contextContainer;
}

@Injectable({
  providedIn: 'root',
})
export class SessionRtdService {
  public rtEventEmitter: EventEmitter<RtEvent[]> = new EventEmitter();
  public rtSingleEventEmitter: EventEmitter<RtEvent> = new EventEmitter();
  private realTimeDrawingHandler?: RealTimeDrawingHandler;
  private realTimeMagicInkHandler?: RealTimeMagicInkHandler;
  private realTimePointerHandler?: RealTimePointerHandler;

  // Emitted when there is a yjs update from the socket server with new scribbles
  public readonly scribblesAdded: Subject<any[]> = new Subject();

  // Holds a reference to the current OverlayCanvas and its render function for RTD.
  overlayCanvas?: OverlayCanvas;
  private readonly overlayCanvasSubject = new BehaviorSubject<OverlayCanvas | undefined>(undefined);
  readonly overlayCanvas$ = this.overlayCanvasSubject.asObservable();
  private renderFunc?: DebouncedFunc<() => void>;

  private subscriptions: Subscription[] = [];
  private visibilityHiddenManually: boolean = false;

  constructor(
    public flagsService: FlagsService,
    public sharedDataService: SessionSharedDataService,
    public sessionsVptService: SessionsVptService,
    private notificationsService: NotificationToasterService,
    private translate: TranslateService,
    private inspectModeRepository: InspectModeRepository,
    private spaceRepo: SpaceRepository,
  ) {
    this.realTimeDrawingHandler = new RealTimeDrawingHandler(
      this.flagsService,
      this.sharedDataService,
      this.sessionsVptService,
      this,
    );
    this.realTimeMagicInkHandler = new RealTimeMagicInkHandler(
      this.flagsService,
      this.sharedDataService,
      this.sessionsVptService,
      this,
    );

    const pointerAnimateDuration =
      (this.flagsService.featureFlagsVariables.event_buffering
        .receive_realtime_buffer_ms as number) ?? 30; // default to 30ms
    this.realTimePointerHandler = new RealTimePointerHandler(
      this.flagsService,
      this.sharedDataService,
      this.sessionsVptService,
      this,
      pointerAnimateDuration,
      this.inspectModeRepository,
      this.spaceRepo,
    );
  }

  private setupEventHandlers() {
    const sub = this.rtEventEmitter.subscribe((rtEvents) => {
      if (this.realTimePointerHandler) {
        this.realTimePointerHandler.handleRTUpdates(rtEvents);
      }
      if (this.realTimeDrawingHandler) {
        this.realTimeDrawingHandler.handleRTUpdates(rtEvents);
      }
      if (this.realTimeMagicInkHandler) {
        this.realTimeMagicInkHandler.handleRTUpdates(rtEvents);
      }
    });
    this.subscriptions.push(sub);
  }

  initSubscriptions() {
    this.setupEventHandlers();
  }

  onDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.realTimePointerHandler?.onDestroy();
    this.realTimeDrawingHandler?.onDestroy();
    this.realTimeMagicInkHandler?.onDestroy();
  }

  setOverlayCanvas(
    overlayCanvas: OverlayCanvas,
    renderFunc?: DebouncedFunc<() => void>,
    originalCanvas?: YCanvas | undefined,
  ) {
    const fabricCanvas = originalCanvas ?? this.sharedDataService.fabricCanvas;
    this.overlayCanvas = overlayCanvas;
    this.overlayCanvasSubject.next(overlayCanvas);
    this.renderFunc = renderFunc;
    fabricCanvas?.on('remote:added', this.handleRemoteAdded.bind(this));
  }

  clearOverlayCanvas(originalCanvas?: YCanvas | undefined) {
    const fabricCanvas = originalCanvas ?? this.sharedDataService.fabricCanvas;
    fabricCanvas?.off('remote:added');
    this.overlayCanvas = undefined;
    this.overlayCanvasSubject.next(undefined);
    this.renderFunc = undefined;
  }

  renderOverlayCanvas() {
    if (this.renderFunc) {
      this.renderFunc();
    }
  }

  private handleRemoteAdded(data: any) {
    const objectsAdded = data.objectsAdded;
    const scribblesAdded = objectsAdded.filter((obj: any) =>
      [ScribbleType.DRAW, ScribbleType.HIGHLIGHT].includes(obj.ScribbleType),
    );
    this.scribblesAdded.next(scribblesAdded);
  }

  togglePointersManually(cursorsHidden: boolean) {
    this.visibilityHiddenManually = cursorsHidden;
    this.togglePointers(cursorsHidden);
  }

  togglePointorsViaPermissions(cursorsHidden: boolean) {
    if (this.visibilityHiddenManually) {
      return;
    }
    this.togglePointers(cursorsHidden);
  }

  private togglePointers(cursorsHidden: boolean) {
    this.realTimePointerHandler?.togglePointers(cursorsHidden);
    this.notificationsService.dismissNotificationsByCode([
      cursorsHidden ? INFOS.SPACE_CURSORS_RESTORED : INFOS.SPACE_CURSORS_HIDDEN,
    ]);
  }

  showCursorNotification() {
    const title = this.translate.instant('Cursors hidden');
    const description = this.translate.instant("Other people's cursors have been hidden for all.");

    const togglePointersNotificationData = new V2NotificationDataBuilder(INFOS.SPACE_CURSORS_HIDDEN)
      .style(ToasterPopupStyle.INFO)
      .type(NotificationType.INFO)
      .timeOut(5)
      .title(title)
      .description(description)
      .dismissable(false)
      .icon({
        assetName: 'hide-cursor',
        type: NotificationIconTypes.SVG_ICON,
        width: 18,
        height: 18,
      })
      .build();
    this.notificationsService.showNotification(togglePointersNotificationData);
  }

  toggleSpecificPointers(userIds: string[], hide: boolean) {
    this.realTimePointerHandler?.toggleSpecificPointers(userIds, hide);
  }

  /** Remove specific users' live pointers outright (DOM + state). Used to clear
   * the AI tutor's cursor on disconnect — it isn't a frame-presence member, so the
   * usual presence-leave cleanup never fires for it. */
  removeUserPointers(userIds: string[]) {
    userIds.forEach((id) => this.realTimePointerHandler?.removePointer(id));
  }
}

// By default the pointer is the same space as the viewport
// This changes the pointer to use the absolute pointer which is
// the point in the transformed canvas that will render in the same place
// as the viewport
// Full Explanation: https://docs.google.com/document/d/1EhfANJTD1153K3RejO6Pb2piUp716AIoe3ZVwe39k2g/edit?usp=sharing
export const scaleEvent = (e: fabric.IEvent) => {
  e.pointer = e.absolutePointer;
  return e;
};
